/**
 * car-motion-geometry.ts — internal segment geometry for CarMotionEngine
 * (PLA-271, ADR-0030). Split out of car-motion-engine.ts to respect the
 * 500-line module cap; consume the engine's public API, not this module.
 *
 * Everything here is pure TypeScript (no DOM, no google.maps, no React).
 * Geometry lives in a local east/north meter plane anchored at the
 * segment-start pose (equirectangular — exact enough for display-scale
 * spans). Heading convention matches car-sprite-engine.ts: compass degrees,
 * 0° = north, increasing clockwise.
 */

import type { CarMotionFix } from "./car-motion-engine"
import { normalizeHeading } from "./car-sprite-engine"

export const METERS_PER_DEG_LAT = 111320
/** Below this displacement a bearing is GPS noise — hold the heading. */
export const MIN_HEADING_DISTANCE_METERS = 1
/** Below this chord a spline buys nothing — animate the straight chord. */
const MIN_SPLINE_CHORD_METERS = 3
/** Hermite tangent magnitude as a fraction of the chord (Catmull-Rom-ish). */
const TANGENT_SCALE = 0.5
/** Each velocity ramp is capped at this fraction of the segment duration. */
const MAX_RAMP_FRACTION = 0.4
/** Samples in the arc-length table (constant-ground-speed reparam). */
const ARC_SAMPLES = 24

/**
 * One in-flight display segment: a straight chord or a Catmull-Rom-style
 * Hermite curve from the segment-start pose (the local origin) to the fix,
 * plus a trapezoidal velocity profile. Treat as opaque.
 */
export interface MotionSegment {
  startMs: number
  durationMs: number
  /** Velocity-profile ramps as fractions of the duration. */
  rampInFraction: number
  rampOutFraction: number
  /** Normalized peak velocity making the profile integrate to 1. */
  peakVelocity: number
  /** Local-plane origin (= displayed pose when the segment started). */
  originLat: number
  originLng: number
  cosOriginLat: number
  /** Segment target in local meters (start is the origin, (0, 0)). */
  endX: number
  endY: number
  kind: "straight" | "spline"
  /** Hermite tangents in local meters (spline only). */
  t1x: number
  t1y: number
  t2x: number
  t2y: number
  /** Chord bearing, or null when too short to define one. */
  chordHeadingDeg: number | null
  totalLengthMeters: number
  /** Cumulative arc length at uniform curve params (spline only). */
  arcLengths: number[]
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** Compass heading of a local east/north vector (x east, y north). */
function headingFromVector(x: number, y: number): number {
  return normalizeHeading((Math.atan2(x, y) * 180) / Math.PI)
}

/** Equirectangular distance in meters — exact enough at display scale. */
export function distanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const cosLat = Math.max(Math.cos((lat1 * Math.PI) / 180), 1e-6)
  const x = (lng2 - lng1) * METERS_PER_DEG_LAT * cosLat
  const y = (lat2 - lat1) * METERS_PER_DEG_LAT
  return Math.hypot(x, y)
}

/** Compass bearing between two points, or null below the noise floor. */
export function bearingBetween(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number | null {
  const cosLat = Math.max(Math.cos((lat1 * Math.PI) / 180), 1e-6)
  const x = (lng2 - lng1) * METERS_PER_DEG_LAT * cosLat
  const y = (lat2 - lat1) * METERS_PER_DEG_LAT
  return Math.hypot(x, y) >= MIN_HEADING_DISTANCE_METERS ? headingFromVector(x, y) : null
}

/** Hermite position + derivative at param t (start point = local origin). */
function hermiteAt(
  seg: MotionSegment,
  t: number,
): { x: number; y: number; dx: number; dy: number } {
  const tt = t * t
  const ttt = tt * t
  const h10 = ttt - 2 * tt + t
  const h01 = -2 * ttt + 3 * tt
  const h11 = ttt - tt
  const d10 = 3 * tt - 4 * t + 1
  const d01 = -6 * tt + 6 * t
  const d11 = 3 * tt - 2 * t
  return {
    x: h10 * seg.t1x + h01 * seg.endX + h11 * seg.t2x,
    y: h10 * seg.t1y + h01 * seg.endY + h11 * seg.t2y,
    dx: d10 * seg.t1x + d01 * seg.endX + d11 * seg.t2x,
    dy: d10 * seg.t1y + d01 * seg.endY + d11 * seg.t2y,
  }
}

/**
 * Fraction of the segment LENGTH covered at time fraction `p` of the
 * duration. Trapezoidal velocity: optional linear ramp-in (standstill start),
 * constant peak velocity mid-drive, linear ramp-out tail (only ever reached
 * on segments with no successor — retargeting replaces them first).
 */
function profileDistance(seg: MotionSegment, p: number): number {
  const a = seg.rampInFraction
  const b = seg.rampOutFraction
  const v = seg.peakVelocity
  if (p <= 0) return 0
  if (p >= 1) return 1
  if (a > 0 && p < a) return (v * p * p) / (2 * a)
  if (p <= 1 - b) return v * (a / 2 + (p - a))
  const r = 1 - p
  return 1 - (v * r * r) / (2 * b)
}

/** Normalized velocity (fraction of length per fraction of duration) at `p`. */
function profileVelocity(seg: MotionSegment, p: number): number {
  const a = seg.rampInFraction
  const b = seg.rampOutFraction
  const v = seg.peakVelocity
  if (p < 0 || p >= 1) return 0
  if (a > 0 && p < a) return (v * p) / a
  if (p <= 1 - b) return v
  return b > 0 ? (v * (1 - p)) / b : v
}

/** Maps an arc-length fraction to the curve param via the sampled table. */
function arcFractionToT(seg: MotionSegment, s: number): number {
  const lengths = seg.arcLengths
  const total = seg.totalLengthMeters
  if (lengths.length < 2 || total <= 0) return s
  const target = s * total
  let low = 0
  let high = lengths.length - 1
  while (low + 1 < high) {
    const mid = (low + high) >> 1
    if ((lengths[mid] ?? 0) <= target) low = mid
    else high = mid
  }
  const l0 = lengths[low] ?? 0
  const l1 = lengths[high] ?? l0
  const span = l1 - l0
  const frac = span > 0 ? clamp((target - l0) / span, 0, 1) : 0
  return (low + frac) / (lengths.length - 1)
}

/** Local position + tangent heading at arc-length fraction `s`. */
function pointAlongSegment(
  seg: MotionSegment,
  s: number,
): { x: number; y: number; headingDeg: number | null } {
  if (seg.kind === "straight" || seg.totalLengthMeters <= 0) {
    return { x: seg.endX * s, y: seg.endY * s, headingDeg: seg.chordHeadingDeg }
  }
  const { x, y, dx, dy } = hermiteAt(seg, arcFractionToT(seg, s))
  const headingDeg = Math.hypot(dx, dy) > 1e-6 ? headingFromVector(dx, dy) : seg.chordHeadingDeg
  return { x, y, headingDeg }
}

export interface DisplayEval {
  lat: number
  lng: number
  headingDeg: number
  speedMps: number
  /** True while the segment has duration left. */
  moving: boolean
  /** True when the geometry yielded a real heading (vs. the held fallback). */
  headingResolved: boolean
}

/** Evaluates the displayed pose at `nowMs` (rest pose when no segment). */
export function displayNow(
  segment: MotionSegment | null,
  restLat: number,
  restLng: number,
  heldHeadingDeg: number,
  nowMs: number,
): DisplayEval {
  if (segment === null) {
    return {
      lat: restLat,
      lng: restLng,
      headingDeg: heldHeadingDeg,
      speedMps: 0,
      moving: false,
      headingResolved: false,
    }
  }
  const p = clamp((nowMs - segment.startMs) / segment.durationMs, 0, 1)
  const point = pointAlongSegment(segment, profileDistance(segment, p))
  const speedMps =
    (profileVelocity(segment, p) * segment.totalLengthMeters) / (segment.durationMs / 1000)
  return {
    lat: segment.originLat + point.y / METERS_PER_DEG_LAT,
    lng: segment.originLng + point.x / (METERS_PER_DEG_LAT * segment.cosOriginLat),
    headingDeg: point.headingDeg ?? heldHeadingDeg,
    speedMps,
    moving: p < 1,
    headingResolved: point.headingDeg !== null,
  }
}

export interface SegmentBuildParams {
  startMs: number
  durationMs: number
  /** Ease-in only when pulling away from a standstill (ADR-0030 §3). */
  easeIn: boolean
  easeRampMs: number
  startLat: number
  startLng: number
  /** Displayed heading to leave along, or null when none is known yet. */
  startHeadingDeg: number | null
  /** Previous raw fix — the arrival tangent points from it to `fix`. */
  prevFix: CarMotionFix
  fix: CarMotionFix
  /** False with < 3 known fixes or for catch-up glides → straight chord. */
  allowSpline: boolean
}

export function buildSegment(params: SegmentBuildParams): MotionSegment {
  const { fix, prevFix } = params
  const cosLat = Math.max(Math.cos((params.startLat * Math.PI) / 180), 1e-6)
  const endX = (fix.lng - params.startLng) * METERS_PER_DEG_LAT * cosLat
  const endY = (fix.lat - params.startLat) * METERS_PER_DEG_LAT
  const chord = Math.hypot(endX, endY)

  const rampOutFraction = Math.min(params.easeRampMs / params.durationMs, MAX_RAMP_FRACTION)
  const rampInFraction = params.easeIn ? rampOutFraction : 0

  const straight: MotionSegment = {
    startMs: params.startMs,
    durationMs: params.durationMs,
    rampInFraction,
    rampOutFraction,
    peakVelocity: 1 / (1 - rampInFraction / 2 - rampOutFraction / 2),
    originLat: params.startLat,
    originLng: params.startLng,
    cosOriginLat: cosLat,
    endX,
    endY,
    kind: "straight",
    t1x: 0,
    t1y: 0,
    t2x: 0,
    t2y: 0,
    chordHeadingDeg: chord >= MIN_HEADING_DISTANCE_METERS ? headingFromVector(endX, endY) : null,
    totalLengthMeters: chord,
    arcLengths: [],
  }

  if (!params.allowSpline || params.startHeadingDeg === null || chord < MIN_SPLINE_CHORD_METERS) {
    return straight
  }

  // Departure tangent: keep moving in the currently displayed direction, so
  // retargets are heading-continuous (the curve's t=0 tangent IS t1).
  const startRad = (params.startHeadingDeg * Math.PI) / 180
  const dir1x = Math.sin(startRad)
  const dir1y = Math.cos(startRad)
  // Only curve when the target is ahead. A fix behind the displayed pose is
  // a genuine reversal — gliding the chord beats looping a spline.
  if (dir1x * endX + dir1y * endY <= 0) {
    return straight
  }

  // Arrival tangent: arrive along the raw direction of travel (prev → new
  // fix), which is what bends the displayed path through corners.
  let dir2x = (fix.lng - prevFix.lng) * METERS_PER_DEG_LAT * cosLat
  let dir2y = (fix.lat - prevFix.lat) * METERS_PER_DEG_LAT
  const dir2Len = Math.hypot(dir2x, dir2y)
  if (dir2Len < MIN_HEADING_DISTANCE_METERS) {
    dir2x = endX / chord
    dir2y = endY / chord
  } else {
    dir2x /= dir2Len
    dir2y /= dir2Len
  }

  const tangentMag = chord * TANGENT_SCALE
  const spline: MotionSegment = {
    ...straight,
    kind: "spline",
    t1x: dir1x * tangentMag,
    t1y: dir1y * tangentMag,
    t2x: dir2x * tangentMag,
    t2y: dir2y * tangentMag,
  }

  // Arc-length table: uniform curve-param samples → cumulative meters, so
  // ticks can hold constant GROUND speed despite the curved path.
  const lengths: number[] = [0]
  let total = 0
  let prevX = 0
  let prevY = 0
  for (let i = 1; i <= ARC_SAMPLES; i++) {
    const pt = hermiteAt(spline, i / ARC_SAMPLES)
    total += Math.hypot(pt.x - prevX, pt.y - prevY)
    lengths.push(total)
    prevX = pt.x
    prevY = pt.y
  }
  if (!Number.isFinite(total) || total <= 0) {
    return straight
  }
  spline.totalLengthMeters = total
  spline.arcLengths = lengths
  return spline
}
